import { gridProductKey } from "../keys";
import { hiddenModeRefusal } from "../validations";
import { readHidden, writeHidden } from "../visibility";
import type { CustomizationHandler } from "./types";

/**
 * The installments line inside product **cards** — the "3x $33,33" under the
 * price of every item in a product grid, carousel or related-products strip.
 *
 * The counterpart of `product-detail-installments`, which is the product *detail*'s
 * payments block. The two are deliberately separate targets: a merchant who
 * wants installments off the grid usually still wants them on the detail, and
 * neither target touches the other's elements.
 *
 * ## The selector
 *
 * `.js-item-product .js-max-installments`, and both halves are load-bearing.
 *
 * **Why the `.js-item-product` scope.** The detail and the card are rendered
 * by the same `our/components/installments.tpl`, so the class on the
 * installments element is byte-identical in both places — there is no class
 * that says "this one is in a card". The discriminator has to be the ancestor.
 * `.js-item-product` is the grid-card wrapper in all 24 SDK-carrying themes,
 * it wraps the installments in every one, and it is absent from the detail
 * form. It also excludes the home page's "main product" block, which lima and
 * rio render with `location: 'product_detail'` *outside* the detail's
 * `.js-product-payments-container` — so scoping by excluding that container
 * instead would have hidden a detail-style block on those two themes.
 *
 * **Why `.js-max-installments` rather than `.js-max-installments-container`.**
 * The container class is the tidier hook and covers 23 of the 24 themes, but
 * cubo's card markup drops it: its grid branch emits
 * `<div class="item-installments">` with no `js-*` hook at all, keeping
 * `.js-max-installments` only on the line inside. Binding to the inner line
 * is what makes cubo work too, at the cost noted below.
 *
 * ## What it matches, per theme
 *
 * On the 20 themes that render cards through the shared component (and
 * ipanema, zenit-flex, atlantico, bahia, which copy it) the outer element
 * carries `js-max-installments-container js-max-installments` *and* wraps an
 * inner `.js-max-installments`, so this selector matches **both**, 2–3
 * elements per card. That is harmless — hiding the outer already hides the
 * inner, and each element carries its own snapshot so `reset` is still exact —
 * but it does mean `applied` counts several elements per card rather than one.
 *
 * On luxury, material, zen and cubo only the inner line matches. Those four
 * wrappers carry no margin of their own except cubo's `.item-installments`,
 * whose `margin-bottom: 5px` survives as a small gap in the card.
 *
 * ## Durability
 *
 * The themes' own JS calls jQuery `.hide()` / `.show()` on these very
 * elements when a shopper picks a variant in a card's quick-shop — an inline
 * `display`, which is exactly what `../visibility.ts` outranks with
 * `!important`. Neither element is recreated, so the class survives; the
 * outer match is the belt to the inner's braces on the themes that have both.
 *
 * ## Per card
 *
 * A resolver runs once per listed product, and `identify` maps its result
 * back to each card through the `data-product-type="list"` card's product id,
 * like `product-grid-item-price` — so an app can hide installments on some
 * products only. An element outside such a card has no key and gets only
 * what applies to every element (static options).
 *
 * Only `hidden` for now: the wording inside is the theme's own phrasing of a
 * price the store did not choose, so `text` is not offered.
 *
 * @see ./README.md
 */
export const productGridInstallments: CustomizationHandler<"product-grid-installments"> =
	{
		host: "storefront",
		selector: ".js-item-product .js-max-installments",

		read(element) {
			// As with `product-detail-installments`: the themes' `.hide()` leaves an
			// inline `display` here, and that is deliberately not part of the
			// snapshot. The SDK never writes `display` on this element, so it
			// has nothing of the theme's to give back. See ../visibility.ts.
			return { hidden: readHidden(element) };
		},

		operation(element, options) {
			if (options.hidden !== undefined) writeHidden(element, options.hidden);
		},

		identify: gridProductKey,

		validate(_element, options) {
			if (options.hidden !== undefined) {
				const refusal = hiddenModeRefusal(options.hidden);
				if (refusal) return `refused "hidden": ${refusal}`;
			}

			return null;
		},
	};
